import uuid from 'react-uuid';
import { isHostedMedia, mediaUuid } from './media';

// Watch-time tracking for documentation videos. A playback session gets one
// session uuid (the licensing server's idempotency key) and accumulates the
// CUMULATIVE second-ranges actually watched, one tuple per playback stretch.
// Re-watching after a jump back deliberately produces an OVERLAPPING tuple —
// the overlaps are the re-watch statistics and must not be merged away.
// Every report resends the full range set; the server keeps the fuller
// cumulative report per session, so resending is always safe.

const REPORT_INTERVAL_MS = 30000;
// timeupdate deltas above this are seeks/stalls, not continuous playback
// (native controls seek in 5s steps and fire `seeking` first; this is a backstop)
const MAX_PLAYBACK_STEP_SECONDS = 5;
const MAX_RANGE_PAIRS = 500;
const MAX_DURATION_SECONDS = 86400;

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Only hosted media lives on the licensing server; anything without a real
// uuid (legacy external mediaLinks) would just be skipped there
export const isTrackableVideo = (media) =>
  media?.mediaType === 'video' &&
  isHostedMedia(media) &&
  UUID_REGEX.test(mediaUuid(media));

// Wire format: integer pairs in playback order, empty pairs dropped, at most
// 500 pairs. Overlapping tuples stay — they carry the re-watch information.
// Beyond the cap, the chronologically adjacent pair with the smallest gap is
// bridged; for overlapping laps the gap is negative, so dense re-watch
// tuples collapse first and coverage is never lost.
export const toReportRanges = (ranges) => {
  const pairs = ranges
    .map(([start, end]) => [Math.floor(start), Math.floor(end)])
    .filter(([start, end]) => end > start);
  while (pairs.length > MAX_RANGE_PAIRS) {
    let closest = 1;
    for (let i = 2; i < pairs.length; i += 1)
      if (pairs[i][0] - pairs[i - 1][1] < pairs[closest][0] - pairs[closest - 1][1])
        closest = i;
    pairs[closest - 1] = [
      Math.min(pairs[closest - 1][0], pairs[closest][0]),
      Math.max(pairs[closest - 1][1], pairs[closest][1]),
    ];
    pairs.splice(closest, 1);
  }
  return pairs;
};

// Attaches playback tracking to a <video> element. `sendReport` is called with
// { sessionUuid, durationSeconds, ranges } on pause/seek/ended/pagehide and on
// a 30s interval — only when the cumulative ranges actually changed. Returns a
// cleanup function.
export const attachWatchTracker = (video, sendReport) => {
  let session = null;

  const startSession = () => {
    session = {
      uuid: uuid(),
      ranges: [],
      lastTime: video.currentTime,
      lastReportedKey: null,
      done: false,
    };
  };

  const recordProgress = () => {
    if (!session || video.seeking) return;
    const current = video.currentTime;
    const delta = current - session.lastTime;
    if (delta > 0 && delta <= MAX_PLAYBACK_STEP_SECONDS) {
      const last = session.ranges[session.ranges.length - 1];
      // Continuous playback extends the current stretch. After a seek the
      // handlers reset lastTime, so a new stretch starts — overlapping the
      // earlier one when the user jumped back to re-watch a segment
      if (last && last[1] === session.lastTime) last[1] = current;
      else session.ranges.push([session.lastTime, current]);
    }
    session.lastTime = current;
  };

  const report = () => {
    if (!session || !Number.isFinite(video.duration)) return;
    const durationSeconds = Math.min(
      Math.round(video.duration),
      MAX_DURATION_SECONDS
    );
    if (durationSeconds < 1) return;
    const ranges = toReportRanges(session.ranges);
    if (!ranges.length) return;
    const key = JSON.stringify(ranges);
    if (key === session.lastReportedKey) return;
    session.lastReportedKey = key;
    sendReport({ sessionUuid: session.uuid, durationSeconds, ranges });
  };

  const flush = () => {
    recordProgress();
    report();
  };

  const onPlay = () => {
    // Replay after `ended` is a fresh playback session (fresh idempotency key)
    if (!session || session.done) startSession();
    session.lastTime = video.currentTime;
  };
  const onTimeUpdate = () => recordProgress();
  const onSeeking = () => {
    // currentTime has already jumped — drop the gap instead of counting it
    if (session) session.lastTime = video.currentTime;
  };
  const onSeeked = () => {
    if (session) session.lastTime = video.currentTime;
    report();
  };
  const onPause = () => flush();
  const onEnded = () => {
    flush();
    if (session) session.done = true;
  };
  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') flush();
  };

  const intervalId = setInterval(flush, REPORT_INTERVAL_MS);
  video.addEventListener('play', onPlay);
  video.addEventListener('timeupdate', onTimeUpdate);
  video.addEventListener('seeking', onSeeking);
  video.addEventListener('seeked', onSeeked);
  video.addEventListener('pause', onPause);
  video.addEventListener('ended', onEnded);
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    flush();
    clearInterval(intervalId);
    video.removeEventListener('play', onPlay);
    video.removeEventListener('timeupdate', onTimeUpdate);
    video.removeEventListener('seeking', onSeeking);
    video.removeEventListener('seeked', onSeeked);
    video.removeEventListener('pause', onPause);
    video.removeEventListener('ended', onEnded);
    window.removeEventListener('pagehide', flush);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
};
